import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  ArrowLeft,
  User,
  Calendar,
  Clock,
  Target,
  Flag,
  Send,
  AlertCircle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { toast } from 'react-toastify';

interface TaskAssignment {
  _id: string;
  task: {
    _id: string;
    title: string;
    description: string;
    dueDate: string;
    priority: string;
    project: {
      _id: string;
      title: string;
    };
  };
  assignedTo: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  assignedBy: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  status: 'pending' | 'accepted' | 'declined';
  message: string;
  response?: string;
  assignedAt: string;
  respondedAt?: string;
}

const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (hostname.includes('webcontainer.io') || hostname.includes('webcontainer-api.io')) {
      const baseUrl = hostname.replace('--5173--', '--5002--');
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};

const TaskAssignment: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState<TaskAssignment | null>(null);
  const [loading, setLoading] = useState(true);
  const [response, setResponse] = useState<string>('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const API_BASE_URL = getApiBaseUrl();

  useEffect(() => {
    if (id) {
      fetchAssignment();
    }
  }, [id]);

  const fetchAssignment = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_BASE_URL}/task-assignments/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!res.ok) {
        throw new Error('Failed to fetch task assignment');
      }

      const data = await res.json();
      setAssignment(data.assignment);
    } catch (err) {
      setError('Failed to load assignment');
      toast.error('Failed to load assignment');
    } finally {
      setLoading(false);
    }
  };

  const handleResponse = async (accepted: boolean) => {
    if (!assignment) return;

    try {
      setSubmitting(true);
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_BASE_URL}/task-assignments/${assignment._id}/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          accepted,
          response
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Failed to respond to assignment');
      }

      const data = await res.json();
      setAssignment(data.assignment);
      toast.success(`Task ${accepted ? 'accepted' : 'declined'} successfully`);
      
      // Redirect to task page if accepted
      if (accepted) {
        setTimeout(() => {
          navigate(`/dashboard/tasks/${assignment.task._id}`);
        }, 1500);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to respond to assignment');
      toast.error(err.message || 'Failed to respond to assignment');
    } finally {
      setSubmitting(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getTimeUntilDue = (dueDate: string) => {
    if (!dueDate) return 'No deadline set';
    
    const days = Math.ceil((new Date(dueDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    if (days < 0) return `${Math.abs(days)} days overdue`;
    if (days === 0) return 'Due today';
    if (days === 1) return 'Due tomorrow';
    return `Due in ${days} days`;
  };

  const getRelativeTime = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  if (!assignment) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Assignment not found</h3>
        <p className="text-gray-600 mb-6">The task assignment you're looking for doesn't exist.</p>
        <Link
          to="/dashboard/tasks"
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Tasks
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Link
          to="/dashboard/tasks"
          className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors duration-200"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Task Assignment</h1>
          <p className="text-gray-600 mt-1">Review and respond to task assignment</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700 text-sm">{error}</span>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold">{assignment.task.title}</h2>
            <p className="text-gray-500 text-sm">{assignment.task.project.title}</p>
          </div>
          <span className={`px-3 py-1 text-sm rounded-full ${
            assignment.status === 'accepted' ? 'bg-green-100 text-green-800' :
            assignment.status === 'declined' ? 'bg-red-100 text-red-800' :
            'bg-yellow-100 text-yellow-800'
          }`}>
            {assignment.status}
          </span>
        </div>

        <p className="text-gray-700">{assignment.task.description}</p>

        <div className="flex gap-4 text-sm text-gray-600">
          {assignment.task.dueDate && (
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {getTimeUntilDue(assignment.task.dueDate)}
            </div>
          )}
          <span className={`px-2 py-1 rounded-full text-xs ${getPriorityColor(assignment.task.priority)}`}>
            {assignment.task.priority}
          </span>
        </div>

        <div className="border-t pt-4 space-y-2">
          <p className="text-sm text-gray-500">
            Assigned by {assignment.assignedBy.firstName} {assignment.assignedBy.lastName} • {getRelativeTime(assignment.assignedAt)}
          </p>
          <p className="text-blue-700 bg-blue-50 p-3 rounded">{assignment.message}</p>
        </div>

        {assignment.status === 'pending' && assignment.assignedTo._id === user?._id && (
          <div className="space-y-4 border-t pt-4">
            <textarea
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              className="w-full p-3 border rounded"
              rows={3}
              placeholder="Optional response message"
            />
            <div className="flex gap-4">
              <button
                onClick={() => handleResponse(true)}
                disabled={submitting}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                {submitting ? 'Processing...' : 'Accept'}
              </button>
              <button
                onClick={() => handleResponse(false)}
                disabled={submitting}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
              >
                {submitting ? 'Processing...' : 'Decline'}
              </button>
            </div>
          </div>
        )}

        {assignment.status !== 'pending' && assignment.response && (
          <div className={`p-4 rounded ${assignment.status === 'accepted' ? 'bg-green-50' : 'bg-red-50'}`}>
            <p className="font-medium text-gray-800">Response:</p>
            <p className="text-gray-700">{assignment.response}</p>
            {assignment.respondedAt && (
              <p className="text-sm text-gray-500 mt-1">
                Responded {getRelativeTime(assignment.respondedAt)}
              </p>
            )}
          </div>
        )}

        <div className="flex justify-between items-center pt-4 border-t">
          <Link to={`/dashboard/projects/${assignment.task.project._id}`} className="text-blue-600 hover:underline">
            View Project Details
          </Link>
          {assignment.status === 'accepted' && (
            <Link
              to={`/dashboard/tasks/${assignment.task._id}`}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <Send className="w-4 h-4" /> Start Task
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskAssignment;